import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, PackageIcon, TruckIcon, HomeIcon, MapPinIcon, CreditCardIcon, TagIcon, ClockIcon, StoreIcon, PhoneIcon, ExternalLinkIcon, NavigationIcon, ChevronRightIcon } from 'lucide-react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { PRODUCT_IMAGES } from '../data/productImages';
export function ThankYouPage() {
  const navigate = useNavigate();
  const [confettiActive, setConfettiActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [showDeliveryTracking, setShowDeliveryTracking] = useState(false);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Mock order data
  const orderData = {
    orderNumber: 'DN38291',
    orderDate: 'December 20, 2024',
    orderTime: '2:30 PM',
    deliveryType: 'pickup',
    estimatedDelivery: 'Same Day',
    deliveryDate: 'December 20, 2024',
    deliveryTime: '6:00 PM - 8:00 PM',
    shippingAddress: {
      name: 'Ahmed Mohammed',
      phone: '+966 50 123 4567',
      address: 'Al Malqa District, Building 123, Apt 4B',
      city: 'Riyadh',
      postalCode: '12345'
    },
    pickupBranch: {
      name: 'Innova Pharmacy - Riyadh Park',
      address: 'Riyadh Park Mall, Al Malaz District, Riyadh',
      city: 'Riyadh',
      district: 'Al Malaz',
      phone: '+966 11 234 5678',
      coordinates: {
        lat: 24.7136,
        lng: 46.6753
      },
      hours: {
        open: '08:00 AM',
        close: '11:00 PM'
      },
      pickupTime: 'Today, 4:00 PM - 8:00 PM'
    },
    paymentMethod: {
      type: 'Credit Card',
      last4: '4242',
      brand: 'Visa'
    },
    items: [{
      id: 1,
      name: 'Optimum Nutrition Gold Standard 100% Whey Protein',
      quantity: 2,
      price: 29.99,
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg'
    }, {
      id: 2,
      name: 'C4 Original Pre Workout Powder',
      quantity: 1,
      price: 19.99,
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg'
    }],
    pricing: {
      subtotal: 79.97,
      shipping: 0,
      discount: 10.0,
      tax: 12.0,
      total: 81.97
    },
    coupon: {
      code: 'WELCOME10',
      discount: 10.0
    }
  };
  const getGoogleMapsLink = () => {
    if (orderData.deliveryType === 'pickup' && orderData.pickupBranch) {
      const {
        lat,
        lng
      } = orderData.pickupBranch.coordinates;
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return null;
  };
  return <HelmetProvider data-id="element-8162" data-mp="ynq09stj862t">
      <SEO title="Order Confirmed - Thank You" description="Your order has been successfully placed. Thank you for shopping with Innova Pharmacy." canonical="/en/thank-you/" hreflangAr="/thank-you/" hreflangEn="/en/thank-you/" hreflangDefault="/thank-you/" data-id="element-8163" data-mp="ynziuvc8zayh" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-8164" data-mp="az6txiu1m3dd">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-8165" data-mp="fy9p8bk80v85">
          <AnnouncementBar data-id="element-8166" data-mp="7w0o096f9o9f" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-8167" data-mp="7u64mzasu47g" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-8168" data-mp="3uatuq03wzmor" />
        </div>
        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-8169" data-mp="3lbymyjl824jc">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-8170" data-mp="3bbfwv732drsz">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-8171" data-mp="37am9qfxg4h15" />
          </div>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Order Confirmed'
        }]} title="Thank You for Your Order" description="Your order has been successfully placed" data-id="element-8172" data-mp="15jr8v9odtrnk" />
          {/* Main Content */}
          <div className="py-8 px-4" data-id="element-8173" data-mp="316zkinosmstx">
            <div className="max-w-5xl mx-auto" data-id="element-8174" data-mp="31a3tdyd7ecqx">
              {/* Success Header */}
              <div className="bg-white rounded-2xl p-6 md:p-8 mb-6 text-center border border-gray-200" data-id="element-8175" data-mp="24k3a2evlsoh0">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce" data-id="element-8176" data-mp="29iy1g57mzlcq">
                  <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-8177" data-mp="2iib0s521qtjq" />
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-8178" data-mp="3oy3rxkgrb7g2">
                  Order Confirmed!
                </h1>
                <p className="text-gray-600 mb-4" data-id="element-8179" data-mp="3tvnc1104yefx">
                  Thank you for your purchase. Your order has been received.
                </p>
                <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2" data-id="element-8180" data-mp="1pt3ptiytep9o">
                  <span className="text-sm text-gray-600" data-id="element-8181" data-mp="23t4tf66gsasu">Order Number:</span>
                  <span className="text-lg font-bold text-[#1E2B55]" data-id="element-8182" data-mp="1yts50gzy2vo5">
                    #{orderData.orderNumber}
                  </span>
                </div>
              </div>
              {/* Main Content Grid */}
              <div className="grid lg:grid-cols-3 gap-6" data-id="element-8183" data-mp="165w1asbyk3hv">
                {/* Left Column - Order Details */}
                <div className="lg:col-span-2 space-y-6" data-id="element-8184" data-mp="1kv29v6wor62f">
                  {/* Delivery Type Badge */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-8185" data-mp="1g292scdqvln6">
                    <div className="flex items-center justify-center gap-3" data-id="element-8186" data-mp="scy20uwe9r91">
                      {orderData.deliveryType === 'delivery' ? <>
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-8187" data-mp="2z1yk7dvh6yp2">
                            <TruckIcon className="w-6 h-6 text-blue-600" data-id="element-8188" data-mp="33t8bsk0l4c3l" />
                          </div>
                          <div className="text-center" data-id="element-8189" data-mp="2q1tow23k3qr9">
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-8190" data-mp="2pz7b3kq2of0l">
                              Home Delivery
                            </h3>
                            <p className="text-sm text-gray-600" data-id="element-8191" data-mp="3htj29lxzfoo8">
                              Your order will be delivered to your address
                            </p>
                          </div>
                        </> : <>
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-8192" data-mp="3dq38cxdas2f8">
                            <StoreIcon className="w-6 h-6 text-green-600" data-id="element-8193" data-mp="1xbl4s4pgmwa1" />
                          </div>
                          <div className="text-center" data-id="element-8194" data-mp="22ch9ulljsoh1">
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-8195" data-mp="2t1j7k6bfhsyz">
                              Store Pickup
                            </h3>
                            <p className="text-sm text-gray-600" data-id="element-8196" data-mp="2oyml24irpcpr">
                              Pick up your order from the selected branch
                            </p>
                          </div>
                        </>}
                    </div>
                  </div>
                  {/* Order Timeline */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-8197" data-mp="37qp23gzbmv0j">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8198" data-mp="37t1k1u52x5dl">
                      <ClockIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8199" data-mp="32xm6z4ewbdi8" />
                      Order Timeline
                    </h2>
                    <div className="space-y-4" data-id="element-8200" data-mp="3vgruxzb28507">
                      <div className="flex items-start gap-4" data-id="element-8201" data-mp="3vhkt4bvydm6q">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-8202" data-mp="3qp9urdgv28ga">
                          <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-8203" data-mp="1wuzoz3feccs" />
                        </div>
                        <div className="flex-1" data-id="element-8204" data-mp="6un1cktvhcu6">
                          <h3 className="font-bold text-gray-900" data-id="element-8205" data-mp="bsybkwo2xpcx">
                            Order Confirmed
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-8206" data-mp="gtvsl5xcsdcu">
                            {orderData.orderDate} at {orderData.orderTime}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4" data-id="element-8207" data-mp="pqwd31haxrb0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-8208" data-mp="ukhe72e6z1ef">
                          <PackageIcon className="w-5 h-5 text-blue-600" data-id="element-8209" data-mp="zl5059nkcs1s" />
                        </div>
                        <div className="flex-1" data-id="element-8210" data-mp="14gbzy6dppclf">
                          <h3 className="font-bold text-gray-900" data-id="element-8211" data-mp="18ks688r8mm16">
                            Processing Order
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-8212" data-mp="3r8h2u4puskj9">
                            We're preparing your items
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4" data-id="element-8213" data-mp="51irr270h6yq">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-8214" data-mp="9uty727wvlq4">
                          {orderData.deliveryType === 'delivery' ? <TruckIcon className="w-5 h-5 text-blue-600" data-id="element-8215" data-mp="itfesrttoqo9" /> : <StoreIcon className="w-5 h-5 text-blue-600" data-id="element-8216" data-mp="nucxr6mnpoqt" />}
                        </div>
                        <div className="flex-1" data-id="element-8217" data-mp="sqk3ozf1z08r">
                          <h3 className="font-bold text-gray-900" data-id="element-8218" data-mp="2pm84d7u23326">
                            {orderData.deliveryType === 'delivery' ? 'Estimated Delivery' : 'Ready for Pickup'}
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-8219" data-mp="2iablshzh22oe">
                            {orderData.deliveryType === 'delivery' ? orderData.deliveryDate : orderData.pickupBranch?.pickupTime}
                          </p>
                          {orderData.deliveryType === 'delivery' && <p className="text-sm text-gray-500" data-id="element-8220" data-mp="35yutowem0dfy">
                              {orderData.deliveryTime}
                            </p>}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Order Items */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-8221" data-mp="2r4px69gvst28">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8222" data-mp="1kk6vqft4b6oz">
                      <PackageIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8223" data-mp="1fpb3bejyyhbc" />
                      Order Items
                    </h2>
                    <div className="space-y-4" data-id="element-8224" data-mp="bp32emb32f85">
                      {orderData.items.map(item => <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-200 last:border-0" data-id="element-8225" data-mp="3tvhdmdhywb2u">
                          <img src={item.image} alt={item.name} className="w-20 h-20 object-contain rounded-lg border border-gray-200" data-id="element-8226" data-mp="3k5encl28db87" />
                          <div className="flex-1" data-id="element-8227" data-mp="3b5rni3p5gdh9">
                            <h3 className="font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-8228" data-mp="3g3babrzwxykt">
                              {item.name}
                            </h3>
                            <p className="text-sm text-gray-600" data-id="element-8229" data-mp="3172aopc41j7v">
                              Quantity: {item.quantity}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-8230" data-mp="2mk3n55gdj8yf">
                            <p className="font-bold text-gray-900" data-id="element-8231" data-mp="2ii9sbdsv0z66">
                              SAR {(item.price * item.quantity).toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-600" data-id="element-8232" data-mp="2wfeu31ljjbvm">
                              SAR {item.price.toFixed(2)} each
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>
                  {/* Shipping Address or Pickup Branch Details */}
                  {orderData.deliveryType === 'delivery' ? <div className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-8233" data-mp="3a8ny0mqpz56r">
                      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8234" data-mp="3p6gepi805zmj">
                        <MapPinIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8235" data-mp="3k7lv4e27o71d" />
                        Shipping Address
                      </h2>
                      <div className="space-y-2" data-id="element-8236" data-mp="1hjx1dxa9pery">
                        <p className="font-bold text-gray-900" data-id="element-8237" data-mp="1mjrt231va8e2">
                          {orderData.shippingAddress.name}
                        </p>
                        <p className="text-gray-700" data-id="element-8238" data-mp="1rbsyp64j50v4">
                          {orderData.shippingAddress.phone}
                        </p>
                        <p className="text-gray-700" data-id="element-8239" data-mp="1wbfgztz7t7as">
                          {orderData.shippingAddress.address}
                        </p>
                        <p className="text-gray-700" data-id="element-8240" data-mp="13lypcyjf5ufm">
                          {orderData.shippingAddress.city},{' '}
                          {orderData.shippingAddress.postalCode}
                        </p>
                      </div>
                    </div> : <div className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-8241" data-mp="2jzymy2usl7zp">
                      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8242" data-mp="2oy07ytbs3hdt">
                        <StoreIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8243" data-mp="dbeqmsku5r0s" />
                        Pickup Branch Details
                      </h2>
                      <div className="space-y-4" data-id="element-8244" data-mp="8cbw422hf372">
                        {/* Branch Name */}
                        <div data-id="element-8245" data-mp="3jirde70juuz">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8246" data-mp="3iqwy6p5k36a">
                            Branch Name
                          </p>
                          <p className="font-bold text-gray-900 text-lg" data-id="element-8247" data-mp="3psjzhkpfe1tq">
                            {orderData.pickupBranch?.name}
                          </p>
                        </div>
                        {/* Location */}
                        <div data-id="element-8248" data-mp="3gs5aobd964lh">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8249" data-mp="3bsio3bbjbc00">Location</p>
                          <div className="flex items-start gap-2" data-id="element-8250" data-mp="3bun2in727tej">
                            <MapPinIcon className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" data-id="element-8251" data-mp="1eyxnygvb8kpk" />
                            <div data-id="element-8252" data-mp="1a275xnzy3f1i">
                              <p className="text-gray-900 font-medium" data-id="element-8253" data-mp="19z2vi8vvzn2e">
                                {orderData.pickupBranch?.address}
                              </p>
                              <p className="text-sm text-gray-600 mt-1" data-id="element-8254" data-mp="398w3mos7yhzl">
                                {orderData.pickupBranch?.district},{' '}
                                {orderData.pickupBranch?.city}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Phone */}
                        <div data-id="element-8255" data-mp="3s78dkcqyfvw8">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8256" data-mp="3s6gj5593g47j">
                            Branch Phone
                          </p>
                          <div className="flex items-center gap-2" data-id="element-8257" data-mp="3i8sk434ggajk">
                            <PhoneIcon className="w-5 h-5 text-gray-600" data-id="element-8258" data-mp="3i6y1ivgnouin" />
                            <a href={`tel:${orderData.pickupBranch?.phone}`} className="text-[#1E2B55] hover:underline font-medium" data-id="element-8259" data-mp="21trcmxnsygjc">
                              {orderData.pickupBranch?.phone}
                            </a>
                          </div>
                        </div>
                        {/* Working Hours */}
                        <div data-id="element-8260" data-mp="xvw0b1f6jn26">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8261" data-mp="tv3pi3qnta3s">
                            Working Hours
                          </p>
                          <div className="flex items-center gap-2" data-id="element-8262" data-mp="66kgflzsctan">
                            <ClockIcon className="w-5 h-5 text-gray-600" data-id="element-8263" data-mp="16obnkj9kxu1" />
                            <p className="text-gray-900 font-medium" data-id="element-8264" data-mp="14lg6kzcechx">
                              {orderData.pickupBranch?.hours.open} -{' '}
                              {orderData.pickupBranch?.hours.close}
                            </p>
                          </div>
                        </div>
                        {/* Pickup Time */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-8265" data-mp="3ek9nl3dg8dk1">
                          <p className="text-sm text-gray-600 mb-1" data-id="element-8266" data-mp="7bhif4xlu5tp">
                            Estimated Pickup Time
                          </p>
                          <p className="text-lg font-bold text-[#1E2B55]" data-id="element-8267" data-mp="cbwttqybt391">
                            {orderData.pickupBranch?.pickupTime}
                          </p>
                        </div>
                        {/* Google Maps Link */}
                        {getGoogleMapsLink() && <a href={getGoogleMapsLink() || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-medium py-3 rounded-lg transition-colors" data-id="element-8268" data-mp="q7hasguhvbdt">
                            <NavigationIcon className="w-5 h-5" data-id="element-8269" data-mp="18xyp3geky4fn" />
                            Get Directions on Google Maps
                            <ExternalLinkIcon className="w-4 h-4" data-id="element-8270" data-mp="1o335pn63bgqv" />
                          </a>}
                      </div>
                    </div>}
                  {/* Payment Method */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200" data-id="element-8271" data-mp="1e5yfnc94j14s">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8272" data-mp="1a3tmeeml86io">
                      <CreditCardIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8273" data-mp="26u40s043as55" />
                      Payment Method
                    </h2>
                    <div className="flex items-center gap-3" data-id="element-8274" data-mp="21v18lg97kgel">
                      <div className="w-12 h-8 bg-blue-100 rounded flex items-center justify-center" data-id="element-8275" data-mp="1x39bsnzgleg0">
                        <CreditCardIcon className="w-6 h-6 text-blue-600" data-id="element-8276" data-mp="1t2z9blavl65w" />
                      </div>
                      <div data-id="element-8277" data-mp="2pkbyu3sxmseh">
                        <p className="font-bold text-gray-900" data-id="element-8278" data-mp="2pln1g0an5u40">
                          {orderData.paymentMethod.brand}{' '}
                          {orderData.paymentMethod.type}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-8279" data-mp="fa4nut3pxhiu">
                          •••• •••• •••• {orderData.paymentMethod.last4}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right Column - Order Summary */}
                <div className="lg:col-span-1" data-id="element-8280" data-mp="1bdflnbjeq2m">
                  <div className="bg-white rounded-xl p-6 sticky top-6 border border-gray-200" data-id="element-8281" data-mp="yvrata5ufk3a">
                    <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-8282" data-mp="txg438auhppa">
                      Order Summary
                    </h2>
                    <div className="space-y-3 mb-4" data-id="element-8283" data-mp="p553s4c701w7">
                      <div className="flex justify-between text-gray-700" data-id="element-8284" data-mp="k8oeh6t9bhhj">
                        <span data-id="element-8285" data-mp="1hnadffvygw4s">Subtotal</span>
                        <span data-id="element-8286" data-mp="1ho2ayw9453xp">SAR {orderData.pricing.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-700" data-id="element-8287" data-mp="1cuzdwe54nccr">
                        <span data-id="element-8288" data-mp="1k8i1ehjkcjf7">Shipping</span>
                        <span className="text-green-600 font-medium" data-id="element-8289" data-mp="1k9t9gtk1kdc4">
                          {orderData.pricing.shipping === 0 ? 'FREE' : `SAR ${orderData.pricing.shipping.toFixed(2)}`}
                        </span>
                      </div>
                      {orderData.coupon && <div className="flex justify-between text-green-600" data-id="element-8290" data-mp="237ci3w2l7ty0">
                          <span className="flex items-center gap-1" data-id="element-8291" data-mp="285e4apvb89dp">
                            <TagIcon className="w-4 h-4" data-id="element-8292" data-mp="283ksyx9r50zd" />
                            Coupon ({orderData.coupon.code})
                          </span>
                          <span data-id="element-8293" data-mp="2hvqnoere1wi9">
                            -SAR {orderData.coupon.discount.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between text-gray-700" data-id="element-8294" data-mp="2qvdn4v0k4s8o">
                        <span data-id="element-8295" data-mp="rreyf4n3t8n6">Tax (VAT 15%)</span>
                        <span data-id="element-8296" data-mp="rs6q44dkym86">SAR {orderData.pricing.tax.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-3" data-id="element-8297" data-mp="mtvjs3mhutup">
                        <div className="flex justify-between items-center" data-id="element-8298" data-mp="10jyz0amyhnan">
                          <span className="text-lg font-bold text-gray-900" data-id="element-8299" data-mp="vt8uezmctoh0">
                            Total
                          </span>
                          <span className="text-2xl font-bold text-[#1E2B55]" data-id="element-8300" data-mp="1aglgqqjzjy8g">
                            SAR {orderData.pricing.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="space-y-3 mt-6" data-id="element-8301" data-mp="1fhhml9nwdugi">
                      <button onClick={() => navigate('/')} className="w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-8302" data-mp="3l9ob4fw9yuzn">
                        <HomeIcon className="w-5 h-5" data-id="element-8303" data-mp="376s5xbz8rtf9" />
                        Continue Shopping
                      </button>
                      <button onClick={() => navigate('/account/orders')} className="w-full border-2 border-[#1E2B55] text-[#1E2B55] hover:bg-blue-50 font-bold py-3 rounded-lg transition-colors" data-id="element-8304" data-mp="2xf4kbrpd96lp">
                        Track Your Order
                      </button>
                    </div>
                    {/* Need Help */}
                    <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-8305" data-mp="2ega5yjtte862">
                      <p className="text-sm text-gray-600 text-center mb-3" data-id="element-8306" data-mp="2jetqqgwo9dwu">
                        Need help with your order?
                      </p>
                      <button className="w-full text-[#1E2B55] hover:text-[#162041] font-medium text-sm" data-id="element-8307" data-mp="cpot4vm8jfvj">
                        Contact Customer Support
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <div className="hidden md:block" data-id="element-8308" data-mp="28sbk12fngvru">
          <Footer data-id="element-8309" data-mp="23rw4q5bt5e78" />
        </div>
        <MobileFooterNav data-id="element-8310" data-mp="23r476oynh6eb" />
        <PharmacistChat data-id="element-8311" data-mp="23uiaq0j1vlnc" />
        <div className="h-16 md:hidden" data-id="element-8312" data-mp="1zr3v3az0x7af"></div>
      </div>
    </HelmetProvider>;
}